import { zodResolver } from '@hookform/resolvers/zod';
import { type ForgotPasswordDto, ForgotPasswordSchema } from '@nestjs-boilerplate/types';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useForm } from 'react-hook-form';

import logo from '@/assets/OKYC logo.webp';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';

import { useForgotPassword } from '../hooks/use-forgot-password';

export function ForgotPasswordForm() {
  const { mutate: submitForgotPassword, isPending, isSuccess } = useForgotPassword();

  const form = useForm<ForgotPasswordDto>({
    resolver: zodResolver(ForgotPasswordSchema),
    defaultValues: {
      email: '',
    },
  });

  function onSubmit(data: ForgotPasswordDto) {
    submitForgotPassword(data);
  }

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-[#F5F7F9]">
      <Link
        to="/login"
        className="fixed left-6 top-6 flex h-10 w-10 items-center justify-center rounded-full border border-[#D4DAE0] text-[#131313]"
      >
        <ChevronLeft className="h-5 w-5" />
      </Link>

      <Card className="w-[390px] min-h-[418px] rounded-[5px] border border-[#D4DAE0] bg-white p-6">
        <CardHeader className="space-y-6 p-0 text-center">
          <img src={logo} alt="OKYC Logo" className="mx-auto h-16 w-auto" />

          <div className="space-y-3">
            <h1 className="text-[20px] font-semibold leading-[120%] text-[#051923]">
              Forgot Password
            </h1>
            <p className="text-left text-[18px] font-normal leading-[140%] text-[#051923]">
              Please enter your account email address so we can begin the password reset process.
            </p>
          </div>
        </CardHeader>

        {isSuccess ? (
          <CardContent className="mt-6 space-y-6 p-0">
            <div className="rounded-[5px] bg-green-50 p-4 text-center">
              <p className="text-[16px] text-green-800">
                If an account with that email exists, we have sent a password reset link. Please
                check your inbox.
              </p>
            </div>
            <Link to="/login" className="mt-4 block">
              <Button
                type="button"
                className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white hover:bg-[#131313]/90"
              >
                Back to Login
              </Button>
            </Link>
          </CardContent>
        ) : (
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
              <CardContent className="mt-6 space-y-6 p-0">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email Address</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="john.smith@company.com"
                          autoComplete="email"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <Button
                  type="submit"
                  className="h-[45px] w-full rounded-[5px] bg-[#131313] text-[18px] font-medium leading-[100%] text-white transition-opacity duration-300 ease-out hover:bg-[#131313]/90"
                  disabled={isPending}
                >
                  {isPending ? 'Sending...' : 'Reset Password'}
                </Button>
              </CardContent>
            </form>
          </Form>
        )}
      </Card>
    </div>
  );
}
